import { Routes, SlateNode } from '@all-hands-hq/plex-graphql-schema';
import { makeStyles } from '@material-ui/core';
import { lightGreen } from '@material-ui/core/colors';
import { EditablePlugins } from '@udecode/slate-plugins';
import classnames from 'classnames';
import isHotkey from 'is-hotkey';
import React, { useCallback, useRef, useState } from 'react';
import { Transforms } from 'slate';
import { Editable, RenderElementProps, RenderLeafProps } from 'slate-react';

import { newLinkEditorMenu } from '../../../../actions/newLinkEditorMenu';
import {
  LinkPreviewMetadataDetailsFragment,
  LinkPreviewMetadataFragment,
  SearchUser,
} from '../../../../graphql/gen/types';
import { usePlexesMap } from '../../../../models/filters/useSelectedPlexIds';
import { useDispatch } from '../../../../redux';
import { colorPalette, spacings } from '../../../../theme';
import { notEmpty } from '../../../../utils/notEmpty';
import { SearchResultOption } from '../../../logged_in/search/useSearch';
import { insertMention, MentionData, MentionsMenu } from '../mentions/MentionsMenu';
import { UserSearchState } from '../mentions/usePlexUserSearch';
import { SelectionMenu } from '../selection_menu/SelectionMenu';
import { EditorButtons } from './buttons';
import { SlatePlugins } from './plugins';
import { SlateElementView } from './SlateElement';
import { SlateLeafView } from './SlateLeaf';
import { usePersistentSelection } from './usePersistentSelection';
import { useSlate } from './useSlate';
import { insertAndSelectElements } from './utils';
import { canLinkifySelection } from './withLinks';

const LINK_HOTKEY = 'mod+k';

type EditableProps = Omit<
  React.ComponentProps<typeof Editable>,
  'renderElement' | 'renderLeaf' | 'onKeyDown' | 'decorate' | 'onDOMBeforeInput'
> & {
  hideImages?: boolean;
  onKeyDownMentions?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

export function WrappedSlateEditable({
  hideImages,
  readOnly,
  onKeyDownMentions,
  className,
  ...other
}: EditableProps): React.ReactElement {
  const editor = useSlate();
  const dispatch = useDispatch();
  const classes = useStyles();

  const renderElement = useCallback(
    // TODO(https://github.com/all-hands-hq/source/issues/1046): use `Slate<SlateElement>`
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (props: RenderElementProps) => <SlateElementView editable={!readOnly} hideImages={hideImages} {...props} />,
    [hideImages, readOnly]
  );
  const renderLeaf = useCallback((props: RenderLeafProps) => <SlateLeafView {...props} />, []);
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      onKeyDownMentions && onKeyDownMentions(event);
      if (event.isDefaultPrevented()) return;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (isHotkey(LINK_HOTKEY, event)) {
        event.preventDefault();
        const { selection } = editor;
        if (selection != null && canLinkifySelection(editor, selection)) {
          dispatch(newLinkEditorMenu(selection));
        }
      }
    },
    [dispatch, editor, onKeyDownMentions]
  );

  return (
    <EditablePlugins
      style={
        {
          // override default `EditablePlugins` style
        }
      }
      {...other}
      className={classnames(classes.rootWrappedSlateEditable, className)}
      renderElement={[renderElement]}
      renderLeaf={[renderLeaf]}
      onKeyDown={[onKeyDown]}
      onKeyDownDeps={[onKeyDown]}
      readOnly={readOnly}
      plugins={SlatePlugins}
    />
  );
}

type State = {
  hasDragOver: boolean;
};

const EMPTY_OPTIONS: SearchUser[] = [];

export function SlateEditableWithControls({
  title = 'Body',
  className,
  primaryLink,
  onChangePrimaryLink,
  mentionData,
  setMentionData,
  search,
  hideControls,
}: {
  title?: string;
  className?: string;
  primaryLink: string | null | undefined;
  onChangePrimaryLink: (primaryLink: string) => void;
  mentionData: MentionData;
  setMentionData: (m: MentionData) => void;
  search: UserSearchState;
  hideControls?: boolean;
}): React.ReactElement {
  const [state, setState] = useState<State>({ hasDragOver: false });
  const editorRef = useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const editor = useSlate();
  const plexesMap = usePlexesMap();
  const { persistedSelection, onFocus, onBlur } = usePersistentSelection(editor);
  // mentions
  const { target, index } = mentionData;
  const options = search.state === 'done' ? search.options : EMPTY_OPTIONS;

  const onKeyDownMentions = useCallback(
    (event) => {
      if (target) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            const prevIndex = index >= options.length - 1 ? 0 : index + 1;
            setMentionData({ ...mentionData, index: prevIndex });
            break;
          case 'ArrowUp':
            event.preventDefault();
            const nextIndex = index <= 0 ? options.length - 1 : index - 1;
            setMentionData({ ...mentionData, index: nextIndex });
            break;
          case 'Tab':
          case 'Enter':
            event.preventDefault();
            Transforms.select(editor, target);
            if (options[index]) insertMention(editor, options[index]);
            setMentionData({ ...mentionData, target: undefined });
            break;
          case 'Escape':
            event.preventDefault();
            setMentionData({ ...mentionData, target: undefined });
            break;
        }
      }
    },
    [editor, index, mentionData, options, setMentionData, target]
  );
  // end mentions
  const onInsertSearchItems = (searchResults: SearchResultOption[]): void => {
    const plexLinks: SlateNode[] = searchResults
      .map((searchResult) => {
        let url: string;
        let details: LinkPreviewMetadataDetailsFragment;
        const plex = plexesMap[searchResult.body?.plexId || ''];

        switch (searchResult.body.__typename) {
          case 'SearchPostBody': {
            url = Routes.routeToUrl({
              route: {
                page: 'feed',
                selectedItem: { type: 'post', permalink: searchResult.body.permalink, commentsVisible: false },
                filters: {},
              },
            });
            details = {
              __typename: 'PlexPostLinkMetadata',
              post: {
                __typename: 'Post',
                id: searchResult.id,
                permalink: searchResult.body.permalink,
                title: searchResult.body.title || '',
                plex: { __typename: 'Plex', id: plex.id, name: plex.name },
              },
            };
            break;
          }
          case 'SearchQuestionBody': {
            url = Routes.routeToUrl({
              route: {
                page: 'base',
                selectedItem: { type: 'question', permalink: searchResult.body.permalink, commentsVisible: false },
                filters: {},
              },
            });
            details = {
              __typename: 'PlexFactLinkMetadata',
              fact: {
                __typename: 'Question',
                id: searchResult.id,
                permalink: searchResult.body.permalink,
                text: searchResult.body?.text || '',
                plex: { __typename: 'Plex', id: plex.id, name: plex.name },
              },
            };
            break;
          }
          default: {
            console.error(`Unknown type in onInsertSearchItems: "${searchResult.body?.__typename}"`);
            return null;
          }
        }
        const linkMetadata: LinkPreviewMetadataFragment = {
          __typename: 'LinkMetadata',
          id: url,
          details,
        };

        editor.setEntities((entities) => ({
          ...entities,
          expanded_link: [...entities.expanded_link, linkMetadata],
        }));
        const plexLink: SlateNode = {
          type: 'expanded_link',
          url,
          children: [{ text: '' }],
        };

        return plexLink;
      })
      .filter(notEmpty);

    insertAndSelectElements(editor, plexLinks, {
      startingAt: persistedSelection || undefined,
      replaceEmptyLine: true,
    });
  };

  return (
    <div ref={editorRef} className={classnames(classes.root, className)} onBlur={onBlur} onFocus={onFocus}>
      <WrappedSlateEditable
        className={classes.bodyRichText}
        spellCheck
        onDragEnter={(): void => {
          setState({ ...state, hasDragOver: true });
        }}
        onDragLeave={(): void => {
          setState({ ...state, hasDragOver: false });
        }}
        onDrop={(): void => {
          setState({ ...state, hasDragOver: false });
        }}
        onKeyDownMentions={onKeyDownMentions}
        placeholder={title}
      />
      <MentionsMenu mentionData={mentionData} setMentionData={setMentionData} editorRef={editorRef} search={search} />

      {!hideControls && (
        <div className={classes.contentFooter}>
          <EditorButtons onInsertSearchItems={onInsertSearchItems} />
          <SelectionMenu
            primaryLink={primaryLink}
            onChangePrimaryLink={onChangePrimaryLink}
            selectionPreservingContainer={editorRef}
          />
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',

    display: 'flex',
    flexDirection: 'column',

    height: '100%',
    overflow: 'hidden',
  },
  rootWrappedSlateEditable: {
    width: '100%',
  },
  bodyRichText: {
    flexGrow: 1,

    overflowY: 'auto',
    paddingTop: spacings.small,
    paddingBottom: spacings.small,
    paddingLeft: spacings.xlarge,
    paddingRight: spacings.xlarge,
  },
  title: {
    backgroundColor: '#fff',
    color: colorPalette.purpleGray._400,
    padding: 2,
  },
  contentFooter: {
    flexGrow: 0,
  },
  noDragover: {
    borderColor: colorPalette.gray._300,
    '&:hover': {
      borderColor: colorPalette.dark._900,
    },
    '&:focus-within': {
      borderColor: colorPalette.primaryColorBlue._500,
    },
  },
  dragover: {
    borderColor: lightGreen[500],
    marginTop: theme.spacing(0.9),
    marginBottom: theme.spacing(0.9),
    paddingLeft: theme.spacing(1.6),
    paddingRight: theme.spacing(1.6),
  },
}));
